import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { MessageDeliveryType } from "../../App/Enums/Message";
import { useCandidateMessageHistory, useGetMessageDetail } from "../../App/Queries/MessageQueries";
import {
  toSelectedCandidate,
  useMessageDialogHandlers,
} from "../../App/State/Hooks/MessageDialog/useMessageDialog";
import {
  ICandidateWithMessageInfo,
  IMessageDetail,
  IMessageHistory,
  IPagination,
} from "../../App/Typings/MessageText";
import { IMessageContainers } from "../../MessageComponents/Enums/MessageEnums";

interface MessagesContextValue {
  selectedCandidate?: ICandidateWithMessageInfo;
  setSelectedCandidate: (c?: ICandidateWithMessageInfo) => void;
  messages: IMessageHistory[];
  messageDataFetched: boolean;
  messageDetail?: IMessageDetail;
  messagePage: number;
  totalMessagePages: number;
  setMessagePage: (page: number) => void;
  selectedMessageId: string;
  setSelectedMessageId: (id: string) => void;
  messageModalOpen: boolean;
  setMessageModalOpen: (b: boolean) => void;
  handleEmailClick: () => void;
}

const messagePageSize = 5;
const MessagesContext = createContext<MessagesContextValue | null>(null);

interface MessagesContextProviderProps {
  children: React.ReactNode;
  previewMessageDetail?: IMessageDetail;
  previewCandidate?: Partial<ICandidateWithMessageInfo>;
}

export function MessagesContextProvider({
  children,
  previewCandidate,
  previewMessageDetail,
}: MessagesContextProviderProps) {
  const [selectedCandidate, setSelectedCandidate] = useState<ICandidateWithMessageInfo | undefined>(
    previewCandidate as ICandidateWithMessageInfo | undefined,
  );
  const [messagePage, setMessagePage] = useState(1);
  const [selectedMessageId, setSelectedMessageId] = useState<string>("");
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const { handleOpenDialog } = useMessageDialogHandlers();

  const { data: messageData, isFetched: messageDataFetched } = useCandidateMessageHistory(
    selectedCandidate?.id ?? "",
    messagePage,
    messagePageSize,
    MessageDeliveryType.Email,
  );
  const { data: messageDetail } = useGetMessageDetail(selectedMessageId ?? "", !previewMessageDetail);
  const messages = useMemo(() => (messageData ? getSortedMessages(messageData) : []), [messageData]);
  const totalMessagePages = useMemo(() => Math.max(messageData?.totalPages ?? 1, 1), [messageData]);

  const handleEmailClick = useCallback(() => {
    if (selectedCandidate) {
      const candidateEntities = {
        [selectedCandidate.id]: toSelectedCandidate(selectedCandidate),
      };
      handleOpenDialog({
        containerType: IMessageContainers.CandidateEmailNow,
        candidateEntities,
        candidateIds: [selectedCandidate.id],
      });
    }
  }, [selectedCandidate?.id, selectedMessageId]);

  const handleToggleModalOpen = useCallback((open: boolean) => {
    if (!open) {
      setSelectedMessageId("");
    }
    setMessageModalOpen(open);
  }, []);

  return (
    <MessagesContext.Provider
      value={{
        selectedCandidate,
        setSelectedCandidate,
        messages,
        messageDataFetched,
        messageDetail: previewMessageDetail ?? messageDetail,
        messagePage,
        totalMessagePages,
        setMessagePage,
        selectedMessageId,
        setSelectedMessageId,
        messageModalOpen,
        setMessageModalOpen: handleToggleModalOpen,
        handleEmailClick,
      }}
    >
      {children}
    </MessagesContext.Provider>
  );
}

export const useMessagesContext = () => {
  const ctx = useContext(MessagesContext);
  if (!ctx) throw new Error("useMessagesContext must be inside MessagesContextProvider");
  return ctx;
};

const getSortedMessages = (messageData: IPagination<IMessageHistory>) => {
  return messageData.items.slice().sort((a, b) => {
    if (a.created > b.created) return -1;
    if (a.created < b.created) return 1;
    return 0;
  });
};
